import { useRef, useState } from "react";
import { PiXFill } from "react-icons/pi";
import DateInput from "../../../components/common/inputs/DateInput";
import UserModal from "../../../components/common/modals/user-modal/UserModal";
import {
  successMessage,
  warningMessage,
} from "../../../utils/toastify-message/ToastifyMessage";

const ROLE_OPTIONS = [
  { value: "seller", label: "مشاور" },
  { value: "affiliate", label: "نماینده" },
  { value: "sub_affiliate", label: "زیرنماینده" },
];

const PLATFORM_OPTIONS = [
  { value: "eitaa", label: "ایتا" },
  { value: "bale", label: "بله" },
  { value: "insta", label: "اینستا" },
];

const inputClassName =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";

const selectClassName =
  "bg-white border border-gray-300 text-gray-900 text-sm rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full p-3 transition duration-200 ease-in-out dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed";

const labelClassName =
  "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

const findUserPlatform = (user) =>
  PLATFORM_OPTIONS.find((item) =>
    (user?.linkCodes || []).some((code) => String(code).startsWith(item.value))
  );

export default function InactiveUserCustomersReportsFilter({
  filterDataHandler,
  activeSearchButton,
}) {
  const userModalRef = useRef();
  const [filters, setFilters] = useState({
    role: "seller",
    platform: "",
    months: 12,
    from: null,
    to: null,
  });
  const [consultant, setConsultant] = useState(null);

  const hasRange = Boolean(filters.from || filters.to);

  const selectConsultant = (data) => {
    setConsultant({ id: data._id, name: data.name });
    successMessage(`کاربر ${data?.name} انتخاب شد`);
  };

  const handleUser = (data) => {
    const userPlatform = findUserPlatform(data);
    if (filters.platform) {
      if (userPlatform?.value !== filters.platform) {
        warningMessage("کاربر انتخاب‌شده مشاور این پلتفرم نیست");
        return;
      }
      selectConsultant(data);
      return;
    }
    if (ROLE_OPTIONS.some((item) => item.value === data?.role)) {
      setFilters((prev) => ({ ...prev, role: data.role }));
      selectConsultant(data);
      return;
    }
    if (userPlatform) {
      setFilters((prev) => ({ ...prev, platform: userPlatform.value }));
      selectConsultant(data);
      return;
    }
    warningMessage("کاربر انتخاب‌شده مشاور، نماینده یا زیرنماینده نیست");
  };

  const deleteUser = () => {
    setConsultant(null);
  };

  const handleRoleChange = (role) => {
    setFilters((prev) => ({ ...prev, role }));
    setConsultant(null);
  };

  const handlePlatformChange = (platform) => {
    setFilters((prev) => ({ ...prev, platform }));
    setConsultant(null);
  };

  const handleSearchClick = () => {
    const months = Number(filters.months);
    if (!hasRange && (!Number.isInteger(months) || months < 1 || months > 120)) {
      warningMessage("تعداد ماه باید عدد صحیح بین ۱ تا ۱۲۰ باشد");
      return;
    }
    filterDataHandler({
      role: filters.role,
      socialGroup: filters.platform || undefined,
      months: hasRange ? undefined : months,
      from: filters.from || undefined,
      to: filters.to || undefined,
      consultantId: consultant?.id,
    });
  };

  return (
    <div className="w-full flex flex-col flex-wrap my-8 gap-6 px-6">
      <UserModal ref={userModalRef} handleUser={handleUser} />

      <div className="w-full flex flex-col sm:flex-row gap-6">
        <DateInput
          label={"آخرین خرید از تاریخ"}
          value={filters.from}
          onChange={(date) => setFilters((prev) => ({ ...prev, from: date }))}
        />
        <DateInput
          label={"آخرین خرید تا تاریخ"}
          value={filters.to}
          onChange={(date) => setFilters((prev) => ({ ...prev, to: date }))}
        />
        <div className="flex flex-col">
          <label htmlFor="inactive-role-select" className={labelClassName}>
            نقش
          </label>
          <select
            id="inactive-role-select"
            value={filters.role}
            disabled={Boolean(filters.platform)}
            onChange={(e) => handleRoleChange(e.target.value)}
            className={selectClassName}
          >
            {ROLE_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label htmlFor="inactive-months-input" className={labelClassName}>
            بدون خرید بیش از (ماه)
          </label>
          <input
            id="inactive-months-input"
            type="number"
            min={1}
            max={120}
            step={1}
            disabled={hasRange}
            value={filters.months}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, months: e.target.value }))
            }
            className={`${inputClassName} disabled:opacity-50 disabled:cursor-not-allowed`}
          />
        </div>
        <div className="relative">
          <span
            onClick={() => userModalRef?.current?.showModal()}
            className="absolute w-full h-full"
          ></span>
          {consultant ? (
            <PiXFill
              className="cursor-pointer absolute left-2 bottom-4 text-red-500"
              onClick={deleteUser}
            />
          ) : null}
          <label className={labelClassName}>نام مشاور</label>
          <input
            id="inactive-consultant-input"
            value={consultant?.name || ""}
            disabled
            className={inputClassName}
          />
        </div>
      </div>
      <div className="w-full flex flex-col sm:flex-row gap-6">
        <div className="flex flex-col">
          <label htmlFor="inactive-platform-select" className={labelClassName}>
            پلتفرم
          </label>
          <select
            id="inactive-platform-select"
            value={filters.platform}
            onChange={(e) => handlePlatformChange(e.target.value)}
            className={selectClassName}
          >
            <option value="">همه</option>
            {PLATFORM_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <button
          disabled={!activeSearchButton}
          onClick={handleSearchClick}
          className="text-white h-10 mt-[30px] bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-8 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        >
          {activeSearchButton ? "جستجو" : "منتظر بمانید..."}
        </button>
      </div>
    </div>
  );
}
